import { CaretDown, CaretRight, Info } from "@phosphor-icons/react"
import React, { useMemo, useState } from "react"

import { Badge } from "~/components/reusable/Badge"
import { Button } from "~/components/design-system/components"
import { api } from "~/utils/api"
import {
  DateFormatType,
  differenceInCalendarDaysUTC,
  formatDate,
  startOfTodayUTC,
} from "~/utils/datetime"
import { formatNumber, formatNumberWithSuffix } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { Loading } from "../reusable/Loading"

type MonthEval = {
  monthStart: string
  label: string
  forecast: number | null
  actual: number
  snapshotDate: string | null
  daysBefore: number | null
  leadDays: number | null
}

/**
 * Cumulative window for one id: the single ~lead-time-ago snapshot's forecast per month vs. actual
 * sales. Empty `months` (short lead times) => not rendered.
 */
type WindowEval = {
  snapshotDate: string | null
  windowStart: string | null
  windowEnd: string | null
  months: { monthStart: string; label: string; forecast: number | null; actual: number }[]
}

type AggregatedMonth = {
  label: string
  monthStart: string
  forecast: number | null
  actual: number
  minDaysBefore: number | null
  maxDaysBefore: number | null
  /** Distinct picked snapshot dates (one per id). A single entry => show the exact date. */
  snapshotDates: Set<string>
}

type AggregatedWindow = {
  /** Per-month forecast/actual summed across the aggregated ids, chronological. */
  months: { monthStart: string; label: string; forecast: number | null; actual: number }[]
  /** Window totals (the sum of the month rows). */
  forecast: number | null
  actual: number
  /** Distinct decision-point snapshot dates across the aggregated ids. */
  snapshotDates: Set<string>
}

/** Sum the per-id (variant or sku) month evals into one row per month (handles group rows). */
const aggregateByMonth = (ids: string[], map: Map<string, MonthEval[]>): AggregatedMonth[] => {
  const byMonth = new Map<string, AggregatedMonth>()

  for (const id of ids) {
    for (const m of map.get(id) ?? []) {
      let agg = byMonth.get(m.monthStart)
      if (!agg) {
        agg = {
          label: m.label,
          monthStart: m.monthStart,
          forecast: null,
          actual: 0,
          minDaysBefore: null,
          maxDaysBefore: null,
          snapshotDates: new Set(),
        }
        byMonth.set(m.monthStart, agg)
      }
      agg.actual += m.actual
      if (m.forecast !== null) agg.forecast = (agg.forecast ?? 0) + m.forecast
      if (m.snapshotDate) agg.snapshotDates.add(m.snapshotDate)
      if (m.daysBefore !== null) {
        agg.minDaysBefore =
          agg.minDaysBefore === null ? m.daysBefore : Math.min(agg.minDaysBefore, m.daysBefore)
        agg.maxDaysBefore =
          agg.maxDaysBefore === null ? m.daysBefore : Math.max(agg.maxDaysBefore, m.daysBefore)
      }
    }
  }

  return Array.from(byMonth.values()).sort((a, b) => a.monthStart.localeCompare(b.monthStart))
}

/** Merge per-id windows, summing each month across ids. Null when no id has a usable window. */
const aggregateWindow = (ids: string[], map: Map<string, WindowEval>): AggregatedWindow | null => {
  const byMonth = new Map<string, { label: string; forecast: number | null; actual: number }>()
  const snapshotDates = new Set<string>()
  let hasAny = false

  for (const id of ids) {
    const w = map.get(id)
    if (!w || w.months.length === 0) continue
    hasAny = true
    if (w.snapshotDate) snapshotDates.add(w.snapshotDate)
    for (const m of w.months) {
      const agg = byMonth.get(m.monthStart) ?? { label: m.label, forecast: null, actual: 0 }
      agg.actual += m.actual
      if (m.forecast !== null) agg.forecast = (agg.forecast ?? 0) + m.forecast
      byMonth.set(m.monthStart, agg)
    }
  }

  if (!hasAny) return null

  const months = Array.from(byMonth.entries())
    .map(([monthStart, v]) => ({ monthStart, ...v }))
    .sort((a, b) => a.monthStart.localeCompare(b.monthStart))
  const forecast = months.reduce<number | null>(
    (acc, m) => (m.forecast === null ? acc : (acc ?? 0) + m.forecast),
    null,
  )
  const actual = months.reduce((acc, m) => acc + m.actual, 0)

  return { months, forecast, actual, snapshotDates }
}

/** Panel-level lead-time label, e.g. "35 days" or "90–130 days" for a group of ids. */
const leadTimeLabel = (ids: string[], map: Map<string, MonthEval[]>): string | null => {
  let min: number | null = null
  let max: number | null = null
  for (const id of ids) {
    const lead = map.get(id)?.find((m) => m.leadDays !== null)?.leadDays ?? null
    if (lead === null) continue
    min = min === null ? lead : Math.min(min, lead)
    max = max === null ? lead : Math.max(max, lead)
  }
  if (min === null) return null
  const range = max === null || min === max ? `${min}` : `${min}–${max}`
  return `${range} days`
}

/** Mean of per-month |forecast - actual| / actual as a %, over months with a forecast. */
const avgDivergencePct = (months: AggregatedMonth[]): number | null => {
  const errs = months
    .filter((m) => m.forecast !== null)
    .map((m) => {
      const diff = (m.forecast ?? 0) - m.actual
      return m.actual > 0 ? Math.abs(diff) / m.actual : m.forecast! > 0 ? 1 : 0
    })
  if (errs.length === 0) return null
  return (errs.reduce((a, b) => a + b, 0) / errs.length) * 100
}

const formatSnapshotDate = (iso: string): string => formatDate(iso, DateFormatType.FullDateShort)

/** Per-month subtext describing which historic snapshot was picked at lead time. */
const snapshotSubtext = (m: AggregatedMonth): string => {
  const dates = [...m.snapshotDates]
  if (dates.length === 0) return "no snapshot"
  const days = m.minDaysBefore
  if (dates.length === 1) {
    return `Forecast from ${formatSnapshotDate(dates[0]!)}${days !== null ? ` · ${days}d before` : ""}`
  }
  // Group rows: snapshots differ per variant — show the spread instead of one exact date.
  if (m.minDaysBefore === null) return `${dates.length} snapshots`
  const range =
    m.maxDaysBefore === null || m.minDaysBefore === m.maxDaysBefore
      ? `${m.minDaysBefore}d`
      : `${m.minDaysBefore}–${m.maxDaysBefore}d`
  return `${dates.length} snapshots · ${range} before`
}

const DiffBadge = ({ forecast, actual }: { forecast: number | null; actual: number }) => {
  if (forecast === null) {
    return (
      <div className="flex items-center justify-center bg-subtle text-fg-subtle">
        <Badge variant="gray" size="xs" className="mt-[1px]">
          <p className="text-xs">N/A</p>
        </Badge>
      </div>
    )
  }
  // Forecast/Sales - 1: relative over/under-forecast. No sales but a forecast caps at +100%.
  const pct = actual > 0 ? forecast / actual - 1 : forecast > 0 ? 1 : 0
  const variant = Math.abs(pct) <= 0.1 ? "green" : Math.abs(pct) <= 0.3 ? "yellow" : "red"
  const sign = pct > 0 ? "+" : ""
  return (
    <div className="flex items-center justify-center bg-subtle text-fg-subtle">
      <Badge variant={variant} size="xs" className="mt-[1px]">
        {sign}
        {formatNumber(pct * 100, 0)}%
      </Badge>
    </div>
  )
}

/** Subtext naming the decision-point snapshot(s) the window forecast was taken from. */
const windowSubtext = (w: AggregatedWindow): string => {
  const dates = [...w.snapshotDates]
  if (dates.length === 0) return "cumulative"
  if (dates.length === 1) return `Forecast taken ${formatSnapshotDate(dates[0]!)}`
  return `Combined from ${dates.length} forecasts (one per variant)`
}

const fmtNum = (n: number | null): string => (n === null ? "-" : formatNumberWithSuffix(n, 0))

/** Shared header row for the 4-column Month/Forecast/Sales/Diff grids of both cards. */
const GridHeader = () => (
  <>
    <span className="text-xs text-fg-muted">Month</span>
    <span className="text-right text-xs text-fg-muted">Forecast</span>
    <span className="text-right text-xs text-fg-muted">{formatName("sale", true, true)}</span>
    <span className="text-right text-xs text-fg-muted">Diff</span>
  </>
)

/** "— the forecast was 59% too high." tail of a worked-example sentence. */
const errorClause = (forecast: number, actual: number): string => {
  if (actual <= 0) return "."
  const pct = forecast / actual - 1
  if (Math.abs(pct) < 0.005) return " , almost exactly right."
  return ` , and the forecast was ${formatNumber(Math.abs(pct) * 100, 0)}% too ${pct > 0 ? "high" : "low"}.`
}

/** One worked-example sentence: which forecast evaluates a month's demand and how it did. */
const evalSentence = (
  label: string,
  snapshotIso: string,
  timing: string,
  forecast: number,
  actual: number,
): string =>
  `To evaluate the demand for ${label}, we look at the forecast from ` +
  `${formatSnapshotDate(snapshotIso)} (${timing}). That forecast predicted ` +
  `${fmtNum(forecast)} units for ${label}; ${fmtNum(actual)} actually sold${errorClause(forecast, actual)}`

/** Up to 2 worked examples for "By month": each month vs. its OWN ~lead-time-before snapshot. */
const monthExamples = (months: AggregatedMonth[]): string[] =>
  months
    .filter((m) => m.forecast !== null && m.snapshotDates.size === 1)
    .slice(0, 2)
    .map((m) =>
      evalSentence(
        m.label,
        [...m.snapshotDates][0]!,
        m.minDaysBefore !== null ? `${m.minDaysBefore} days before` : "at lead time",
        m.forecast!,
        m.actual,
      ),
    )

/** Up to 2 worked examples for "Cumulative to date": each month vs. the SAME single snapshot. */
const windowExamples = (window: AggregatedWindow): string[] => {
  if (window.snapshotDates.size !== 1) return []
  const snapshotIso = [...window.snapshotDates][0]!
  const daysAgo = differenceInCalendarDaysUTC(startOfTodayUTC(), new Date(snapshotIso))
  return window.months
    .filter((m) => m.forecast !== null)
    .slice(0, 2)
    .map((m) => evalSentence(m.label, snapshotIso, `${daysAgo} days ago`, m.forecast!, m.actual))
}

/** Collapsible per-card explainer; the caller supplies the card-specific paragraphs. */
const HowThisWorks = ({ children }: { children: React.ReactNode }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="mt-2">
      <Button
        variant="transparent"
        size="2xs"
        className="-ml-1"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <CaretDown /> : <CaretRight />}
        How this works
        <Info />
      </Button>
      {expanded && (
        <div className="mt-1 rounded-[10px] border border-border-base bg-base p-2.5 paragraph-sm text-fg-subtle">
          {children}
        </div>
      )}
    </div>
  )
}

/** Per-arrival-month breakdown: each month vs. the forecast from its OWN decision point. */
const MonthBreakdown = ({
  months,
  leadLabel,
}: {
  months: AggregatedMonth[]
  leadLabel: string | null
}) => {
  const avgDivergence = avgDivergencePct(months)
  return (
    <div className="mt-3 rounded-[10px] border border-border-base bg-subtle p-2.5">
      <span className="label-base">By month</span>
      <p className="mb-2 mt-0.5 paragraph-sm text-fg-subtle">
        Stock for a month is ordered ~{leadLabel ?? "one lead time"} before it. Each row compares
        the forecast you had at that point with what the month actually sold.
      </p>
      <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 gap-y-1.5 tabular-nums">
        <GridHeader />
        {months.map((m) => (
          <React.Fragment key={m.monthStart}>
            <span className="flex flex-col leading-tight text-fg-subtle">
              <span>{m.label}</span>
              <span className="text-xs text-fg-muted">{snapshotSubtext(m)}</span>
            </span>
            <span className="text-right text-fg-subtle">{fmtNum(m.forecast)}</span>
            <span className="text-right text-fg-subtle">{fmtNum(m.actual)}</span>
            <span className="flex justify-end">
              <DiffBadge forecast={m.forecast} actual={m.actual} />
            </span>
          </React.Fragment>
        ))}
      </div>
      {avgDivergence !== null && (
        <p className="mt-2 text-xs text-fg-muted">
          Avg forecast error · {formatNumber(avgDivergence, 0)}%
        </p>
      )}
      <HowThisWorks>
        <p>
          Each row compares the demand you had ~{leadLabel ?? "one lead time"} before the month with
          what actually sold in that month.
        </p>
        {monthExamples(months).map((s) => (
          <p key={s} className="mt-1.5">
            {s}
          </p>
        ))}
        <p className="mt-1.5 font-medium">Same time interval, multiple forecast points.</p>
      </HowThisWorks>
    </div>
  )
}

/**
 * Cumulative window: the single ~lead-time-ago forecast vs. everything sold since. Total row always
 * shows; the per-month breakdown is collapsed behind the toggle.
 */
const WindowSummary = ({
  window,
  leadLabel,
}: {
  window: AggregatedWindow
  leadLabel: string | null
}) => {
  const [expanded, setExpanded] = useState(false)
  const monthCount = window.months.length
  return (
    <div className="mt-3 rounded-[10px] border border-border-base bg-subtle p-2.5">
      <span className="label-base">Cumulative to date</span>
      <p className="mb-2 mt-0.5 paragraph-sm text-fg-subtle">
        An order placed ~{leadLabel ?? "one lead time"} ago would arrive now. This compares the
        forecast that order was placed on with everything sold since.
      </p>
      {monthCount > 0 && (
        <Button
          variant="transparent"
          size="2xs"
          className="-ml-1 mb-1"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <CaretDown /> : <CaretRight />}
          {expanded
            ? "Hide monthly breakdown"
            : `Show ${monthCount} month${monthCount === 1 ? "" : "s"}`}
        </Button>
      )}
      {/* Same column layout as the "By month" card — headers stay visible when collapsed. */}
      <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 gap-y-1 tabular-nums">
        <GridHeader />
        {expanded && (
          <>
            {window.months.map((m) => (
              <React.Fragment key={m.monthStart}>
                <span className="text-fg-subtle">{m.label}</span>
                <span className="text-right text-fg-subtle">{fmtNum(m.forecast)}</span>
                <span className="text-right text-fg-subtle">{fmtNum(m.actual)}</span>
                <span className="flex justify-end">
                  <DiffBadge forecast={m.forecast} actual={m.actual} />
                </span>
              </React.Fragment>
            ))}
            <span className="col-span-4 mt-0.5 border-t border-border-base" />
          </>
        )}
        <span className="font-medium">Total</span>
        <span className="text-right font-medium">{fmtNum(window.forecast)}</span>
        <span className="text-right font-medium">{fmtNum(window.actual)}</span>
        <span className="flex justify-end">
          <DiffBadge forecast={window.forecast} actual={window.actual} />
        </span>
      </div>
      <p className="mt-2 text-xs text-fg-muted">{windowSubtext(window)}</p>
      <HowThisWorks>
        <p>
          This compares the forecast from ~{leadLabel ?? "one lead time"} ago against everything
          sold since then up until now.
        </p>
        {windowExamples(window).map((s) => (
          <p key={s} className="mt-1.5">
            {s}
          </p>
        ))}
        <p className="mt-1.5 font-medium">Single forecast point, different time intervals.</p>
      </HowThisWorks>
    </div>
  )
}

/**
 * Lead-time forecast performance inside the forecast-quality hover panel. Two cards:
 *  - By month: the 3 most recent CLOSED months, each vs. the forecast from ~lead time before it.
 *  - Cumulative window: the single ~lead-time-ago snapshot vs. sales over its elapsed horizon
 *    (hidden when empty — short lead times).
 * Lazily fetches on open. `skuId` scopes to that SKU (forecast = variant x split weight); otherwise
 * aggregates the given variants. `warehouseIds` scopes BOTH forecast and sales symmetrically.
 */
const LeadTimePerformanceSection = ({
  variantIds,
  skuId,
  warehouseIds,
}: {
  variantIds: string[]
  skuId?: string
  warehouseIds?: string[]
}) => {
  const skuMode = skuId !== undefined
  // The grid passes one variantId per leaf SKU — dedupe or each variant gets summed N times.
  const uniqueVariantIds = useMemo(() => [...new Set(variantIds)], [variantIds])
  // Sort for a stable query key regardless of arrival order.
  const scopedWarehouseIds = useMemo(() => [...(warehouseIds ?? [])].sort(), [warehouseIds])

  const variantQuery = api.forecastEvaluation.leadTimeEvaluation.useQuery(
    { variantIds: uniqueVariantIds, warehouseIds: scopedWarehouseIds },
    { enabled: !skuMode && uniqueVariantIds.length > 0, staleTime: 5 * 60 * 1000 },
  )
  const skuQuery = api.forecastEvaluation.leadTimeEvaluationBySku.useQuery(
    { skuIds: skuId ? [skuId] : [], warehouseIds: scopedWarehouseIds },
    { enabled: skuMode, staleTime: 5 * 60 * 1000 },
  )
  const { data: warehouses } = api.warehouse.get.useQuery(undefined, { staleTime: 60 * 60 * 1000 })

  const { data, isLoading, isError } = skuMode ? skuQuery : variantQuery
  const ids = skuMode ? (skuId ? [skuId] : []) : uniqueVariantIds

  if (ids.length === 0) return null

  // Lead time is overarching — it anchors both cards, so show it once at the top. Sourced from the
  // per-month data (present whenever anything renders), not the window (which can be empty).
  const leadLabel = leadTimeLabel(ids, data?.months ?? new Map())
  const warehouseNames = scopedWarehouseIds.map(
    (id) => warehouses?.find((w) => w.id === id)?.name ?? id,
  )
  const scopeNote = warehouseNames.length
    ? `Demand for ${warehouseNames.join(", ")} · all channels.`
    : "Total demand across all channels and warehouses."

  return (
    <div className="mt-3 border-t border-border-base pt-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="label-base">Forecast at lead time {skuMode ? "(SKU)" : ""}</span>
        {leadLabel && <span className="text-xs text-fg-muted">Lead time · {leadLabel}</span>}
      </div>
      {isLoading ? (
        <Loading />
      ) : isError ? (
        <p className="paragraph-sm text-fg-subtle">Couldn&apos;t load lead-time performance.</p>
      ) : (
        (() => {
          const months = aggregateByMonth(ids, data?.months ?? new Map())
          const windowAgg = aggregateWindow(ids, data?.window ?? new Map())
          if (months.length === 0) {
            return <p className="paragraph-sm text-fg-subtle">No historic forecast available.</p>
          }
          return (
            <>
              <MonthBreakdown months={months} leadLabel={leadLabel} />
              {windowAgg && <WindowSummary window={windowAgg} leadLabel={leadLabel} />}
              <p className="mt-2 text-xs text-fg-muted">{scopeNote}</p>
            </>
          )
        })()
      )}
    </div>
  )
}

export default LeadTimePerformanceSection
